import NaluParser from './nalu-parser';
import ExpGolomb from './exp-golomb';
import Log from '../utils/logger';

class H264NaluParser extends NaluParser {
    static parseSliceHeader(uint8array: Uint8Array, nal_unit_type: number, spsMap: Map<number, any>, ppsMap: Map<number, any>) {
        let slice: any = {};

        let rbsp = H264NaluParser._ebsp2rbsp(uint8array);
        let gb = new ExpGolomb(rbsp);

        gb.readByte(); // skip nal unit header

        let first_mb_in_slice = slice.first_mb_in_slice = gb.readUEG();
        let slice_type = slice.slice_type = gb.readUEG();
        let pic_parameter_set_id = slice.pic_parameter_set_id = gb.readUEG();

        let slice_type_string = slice.slice_type_string = H264NaluParser.getSliceTypeString(slice_type);

        let pps = ppsMap.get(pic_parameter_set_id);
        if (!pps) {
            Log.w('H264NaluParser', `can not find PPS with slice's pic_parameter_set_id ${pic_parameter_set_id}`);

            gb.destroy();
            gb = null;

            return slice;
        }

        let sps = spsMap.get(pps.seq_parameter_set_id);
        if (!sps) {
            Log.w('H264NaluParser', `can not find SPS with PPS's seq_parameter_set_id ${pps.seq_parameter_set_id}`);

            gb.destroy();
            gb = null;

            return slice;
        }

        if (sps.separate_colour_plane_flag === 1) {
            slice.colour_plane_id = gb.readBits(2); // colour_plane_id
        }

        slice.frame_num = gb.readBits(sps.log2_max_frame_num_minus4 + 4);

        if (!sps.frame_mbs_only_flag) {
            slice.field_pic_flag = gb.readBits(1);
            if (slice.field_pic_flag) { // field_pic_flag
                slice.bottom_field_flag = gb.readBits(1); // bottom_field_flag
            }
        }

        const IdrPicFlag = nal_unit_type === 5 ? 1 : 0;
        if (IdrPicFlag) {
            slice.idr_pic_id = gb.readUEG(); // idr_pic_id
        }

        let pic_order_cnt_type = slice.pic_order_cnt_type = sps.pic_order_cnt_type;
        let pic_order_cnt_lsb = 0;
        if (pic_order_cnt_type === 0) {
            pic_order_cnt_lsb = gb.readBits(sps.log2_max_pic_order_cnt_lsb_minus4 + 4);
            if (pps.bottom_field_pic_order_in_frame_present_flag && !slice.field_pic_flag) {
                slice.delta_pic_order_cnt_bottom = gb.readSEG(); // delta_pic_order_cnt_bottom
            }
        }
        slice.pic_order_cnt_lsb = pic_order_cnt_lsb;

        if (pic_order_cnt_type === 1 && !sps.delta_pic_order_always_zero_flag) {
            gb.readSEG(); // delta_pic_order_cnt[0]
            if (pps.bottom_field_pic_order_in_frame_present_flag && !slice.field_pic_flag) {
                gb.readSEG(); // delta_pic_order_cnt[1]
            }
        }
        if (pps.redundant_pic_cnt_present_flag) {
            slice.redundant_pic_cnt = gb.readUEG(); // redundant_pic_cnt
        }
        if (slice_type_string === 'B') {
            // skip direct_spatial_mv_pred_flag
            slice.direct_spatial_mv_pred_flag = gb.readBits(1);
        }
        if (slice_type_string === 'P' || slice_type_string === 'SP' || slice_type_string === 'B') {
            // skip num_ref_idx_active_override_flag
            let num_ref_idx_active_override_flag = slice.num_ref_idx_active_override_flag = gb.readBits(1);
            if (num_ref_idx_active_override_flag) {
                slice.num_ref_idx_l0_active_minus1 = gb.readUEG(); // num_ref_idx_l0_active_minus1
                if (slice_type_string === 'B') {
                    slice.num_ref_idx_l1_active_minus1 = gb.readUEG(); // num_ref_idx_l1_active_minus1
                }
            }
        }

        slice.parsed = true;

        // TODO: parse more data
        if (nal_unit_type === 20) {
            // ref_pic_list_mvc_modification()
        } else {
            // ref_pic_list_modification()
        }

        gb.destroy();
        gb = null;

        return slice;
    }

    static getSliceTypeString(slice_type: number) {
        switch (slice_type) {
            case 0:
            case 5:
                return 'P';
            case 1:
            case 6:
                return 'B';
            case 2:
            case 7:
                return 'I';
            case 3:
            case 8:
                return 'SP';
            case 4:
            case 9:
                return 'SI';
            default:
                return 'Unknown';
        }
    }

    static getNaluTypeName(nalu_type: number) {
        switch (nalu_type) {
            case 1: return 'NDR';
            case 5: return 'IDR';
            case 6: return 'SEI';
            case 7: return 'SPS';
            case 8: return 'PPS';
            case 9: return 'AUD';
            default: return 'UNKNOWN';
        }
    }
}

export default H264NaluParser;
